import { Link } from "react-router-dom";
import { Users, Sparkles, Clock, ArrowRight, Phone, ExternalLink, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const IMG = "/assets/cube_hand.png";
const CATALOG_URL = "https://www.annapolis.gov/354/Camp-Information";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

export default function SummerCamp() {
  const [camp, setCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/events/public?kind=camp")
      .then((r) => setCamp((r.data || [])[0] || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return (
    <div>
      {/* Detailed Summer Camp overview */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Summer Camp</span>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">Summer Camp</h1>

          <div className="mt-6 space-y-5 text-[#1E1E24] leading-relaxed">
            <p>
              We also offer a <b>week-long summer camp</b>, in July at <b>Pip Moyer Recreation Center</b> in Annapolis. Campers will learn cubing techniques for the <b>2×2 and 3×3 cube</b> in a safe, fun and collaborative environment. If your speed time is greater than 90 seconds, you may want to consider our summer camp. Our summer camp concludes at the end of the week with a small camp competition for those campers interested in competing.
            </p>

            {!loading && camp && (
              <div className="rounded-2xl bg-[#FDE74C]/25 border border-[#FDE74C]/40 px-5 py-4" data-testid="camp-next-card">
                <div className="text-xs uppercase tracking-wider font-semibold text-[#8A5A17]">Upcoming Camp</div>
                <div className="mt-1 font-display font-black text-lg" data-testid="camp-dates">
                  {fmtDate(camp.start_date)}{camp.end_date && camp.end_date !== camp.start_date ? ` – ${fmtDate(camp.end_date)}` : ""}{(camp.start_time || camp.end_time) ? `, ${camp.start_time || ""}${camp.end_time ? ` – ${camp.end_time}` : ""}` : ""}
                </div>
                {camp.title && <div className="mt-1 font-semibold text-[#7A5F00]">{camp.title}</div>}
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-[#1E1E24]">
                  {camp.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {camp.location}</span>}
                </div>
                {camp.description && <p className="mt-3 text-sm whitespace-pre-wrap">{camp.description}</p>}
              </div>
            )}

            {!loading && !camp && (
              <div className="rounded-2xl border-2 border-dashed border-slate-200 px-5 py-4 text-[#7A7D8C]" data-testid="camp-empty">
                Dates for our next Summer Camp will be posted here soon. <Link to="/contact" className="text-[#FF9F1C] font-semibold">Contact us</Link> to be notified when registration opens.
              </div>
            )}

            <p>
              For information on our Summer Camp, please click the link below, which will take you to the Pip Moyer Recreation Site. Open the Summer Brochure and proceed to the Splash Camps. Our Rubik's Cube camp is a <b className="underline decoration-[#FF9F1C] decoration-2 underline-offset-4">Splash Camp</b>. The first half of the day is focused on the Rubik's cube. There is a one-hour lunch and then the second part of the day is spent at the new Kenneth Dunn Municipal Pool at Truxton Park, sponsored by the City of Annapolis.
            </p>

            <p className="italic font-semibold text-[#1E1E24]">
              *Camp registration can be done in person at Pip Moyer Recreation Center or you can click the link below and go to "Camps" and then "Splash Camp" to register online or call directly:{" "}
              <a href="tel:+14102637958" data-testid="camp-phone" className="inline-flex items-center gap-1 text-[#FF9F1C] hover:underline"><Phone className="w-4 h-4" /> (410) 263-7958</a>
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={CATALOG_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="camp-catalog-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)] hover:-translate-y-0.5 transition-transform"
            >
              Camp Information – City of Annapolis Recreation and Parks <ExternalLink className="w-4 h-4" />
            </a>
            <Link to="/contact" data-testid="camp-ask-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 font-bold hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors">Ask a question</Link>
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 rotate-3 rounded-[40px] bg-[#FDE74C]/35" aria-hidden />
          <img src={IMG} alt="Rubik's cube tossed above an outstretched hand" className="relative rounded-[36px] w-full h-[620px] object-cover shadow-[0_20px_60px_rgb(0,0,0,0.15)]" data-testid="camp-hero-image" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-14 grid md:grid-cols-3 gap-6">
        <Detail icon={<Users className="w-5 h-5" />} title="Who it's for" text="Beginners through intermediate cubers. If your average is above 90 seconds, camp is a perfect next step." />
        <Detail icon={<Sparkles className="w-5 h-5" />} title="What they'll learn" text="Cubing techniques for both the 2×2 and 3×3 cube, notation, and pattern-based problem solving." />
        <Detail icon={<Clock className="w-5 h-5" />} title="Dates & times" text={camp ? `${fmtDate(camp.start_date)}${camp.end_date && camp.end_date !== camp.start_date ? ` – ${fmtDate(camp.end_date)}` : ""}${(camp.start_time || camp.end_time) ? `, ${camp.start_time || ""}${camp.end_time ? ` – ${camp.end_time}` : ""}` : ""}${camp.location ? ` at ${camp.location}` : ""}.` : "Dates for our next camp will be announced soon."} />
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-24">
        <div className="rounded-[36px] bg-[#FFBF69]/25 p-8 md:p-12 border border-[#FFBF69]/40">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">A typical camp day</h2>
              <p className="mt-4 text-[#1E1E24]/90 max-w-md leading-relaxed">Cube skills in the morning, pool in the afternoon. Every day ends with a personal-best cheer.</p>
            </div>
            <ul className="space-y-3">
              {[
                { t: "9:00", d: "Arrival & warm-up scrambles at Pip Moyer" },
                { t: "10:00", d: "Cube skill session — 2×2 and 3×3 techniques" },
                { t: "12:00", d: "One-hour lunch" },
                { t: "1:00", d: "Pool time at Kenneth Dunn Municipal Pool, Truxton Park" },
                { t: "3:45", d: "Wind-down & personal-best cheers" },
              ].map((r) => (
                <li key={r.t} className="flex items-baseline gap-4 bg-white/70 rounded-2xl px-5 py-3 border border-white">
                  <span className="font-display font-black text-[#FF9F1C] w-14">{r.t}</span>
                  <span className="text-[#1E1E24]">{r.d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

function Detail({ icon, title, text }) {
  return (
    <div className="bg-white rounded-3xl p-7 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="w-11 h-11 rounded-2xl bg-[#FF9F1C] text-white grid place-items-center">{icon}</div>
      <h3 className="mt-4 font-display font-bold text-lg">{title}</h3>
      <p className="mt-2 text-sm text-[#7A7D8C] leading-relaxed">{text}</p>
    </div>
  );
}
